import React from 'react';
import { Button } from './ui/button';
import { Music, Play, Apple } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';
import { useConfig } from '../config/useConfig';

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language].hero;
  const config = useConfig();

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/80 to-background z-10" />
        <div 
          className="w-full h-full bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${config.images.heroBackground})`,
            filter: 'brightness(0.4) contrast(1.2)'
          }}
        />
      </div>

      {/* Glowing Eclipse Effect */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-64 h-64 bg-secondary/10 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }} />
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
        <div className="animate-fade-in-up">
          {/* Main Title */}
          <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold tracking-wider mb-6">
            <span className="block text-glow-divine" style={{ color: 'hsl(42 50% 52%)' }}>
              {t.title1}
            </span>
            <span className="block text-glow-fire" style={{ color: 'hsl(6 55% 45%)' }}>
              {t.title2}
            </span>
          </h1>

          {/* Subtitle */}
          <div className="mb-4">
            <p className="text-2xl sm:text-3xl md:text-4xl font-light tracking-widest text-foreground/90">
              {t.subtitle}
            </p>
          </div>

          {/* Claim */}
          <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-12 max-w-2xl mx-auto font-light tracking-wide">
            {t.tagline}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              className="divine-glow text-lg px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
              onClick={() => window.open(config.links.spotifyArtist, '_blank')}
            >
              <Music className="mr-2 h-5 w-5" />
              {t.listenSpotify}
            </Button>
            
            <div className="flex gap-4">
              <Button
                variant="outline"
                size="lg"
                className="border-secondary/50 text-secondary hover:bg-secondary/10 hover:border-secondary fire-glow"
                onClick={() => window.open(config.links.youtube, '_blank')}
              >
                <Play className="mr-2 h-5 w-5" />
                {t.youtube}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="border-accent/50 text-accent hover:bg-accent/10 hover:border-accent soul-glow"
                onClick={() => window.open(config.links.appleMusic, '_blank')}
              >
                <Apple className="mr-2 h-5 w-5" />
                {t.appleMusic}
              </Button>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="mt-20 animate-bounce">
            <div className="w-6 h-10 border-2 border-primary/50 rounded-full mx-auto relative">
              <div className="w-1.5 h-3 bg-primary rounded-full absolute top-2 left-1/2 -translate-x-1/2 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;